import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
} from "@mui/material";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateUserAccount,
  DeleteUserAccountSlice,
  GetUserAccounts,
  setFilters,
  setPage,
  setRowsPerPage,
} from "../../redux/slices/createUserAccount";
import { useForm, Controller } from "react-hook-form";
import RefreshIcon from "@mui/icons-material/Refresh";
import "jspdf-autotable";
import moment from "moment";
import UserFormEdit from "./dialog";
import LazyTable from "./lazytable";

export function CreateAccount() {
  const rotate = `
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  `;
  const user = useSelector((state) => state.login);
  const { page, rowsPerPage, totalAccounts, data, filters, loading } =
    useSelector((state) => state.createuser);
  const dispatch = useDispatch();
  const [isAdding, setIsAdding] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("pdf");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const accounts = data?.data || [];

  const handleRefresh = async () => {
    dispatch(GetUserAccounts(page, rowsPerPage, filters));
  };

  useEffect(() => {
    dispatch(GetUserAccounts(page, rowsPerPage, filters));
  }, [dispatch, page, rowsPerPage, filters]);

  // Format accounts for DataTable
  const formattedAccounts = useMemo(
    () =>
      accounts.map((account, index) => ({
        srNo: index + 1,
        userName: account.name,
        userEmail: account.email,
        userRole: account.role,
        userShift: account.shift,
        createdAt: new Date(account.createdAt).toLocaleDateString(),
        _id: account._id,
      })),
    [accounts]
  );

  const headers = ["#", "User Name", "Email", "Role", "Shift", "Created At"];

  const tableData = data?.map((account, index) => [
    index + 1,
    account.name,
    account.email,
    account.role,
    account.shift,
    new Date(account.createdAt).toLocaleDateString(),
    ,
  ]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userName: "",
      userEmail: "",
      userPassword: "",
      userRole: "",
      userShift: "",
    },
  });

  const onSubmit = async (formData) => {
    setIsAdding(true);
    const payload = {
      name: formData.userName,
      email: formData.userEmail,
      password: formData.userPassword,
      role: formData.userRole,
      shift: formData.userShift,
    };

    await dispatch(CreateUserAccount(payload));

    reset();
    setIsAdding(false);
  };
  const [availableRoles, setAvailableRoles] = useState([]);
  const Roles = [
    {
      role: "admin",
    },
    {
      role: "manager",
    },
    {
      role: "designer",
    },
    {
      role: "redeem_agent",
    },
    {
      role: "deposit_agent",
    },
  ];

  useEffect(() => {
    if (user?.user?.role?.toLowerCase() === "admin") {
      setAvailableRoles(Roles);
    } else if (user?.user?.role?.toLowerCase() === "manager") {
      const roleIndex = Roles.findIndex(
        (r) => r.role.toLowerCase() === user?.user?.role?.toLowerCase()
      );
      if (roleIndex !== -1) {
        setAvailableRoles(Roles.slice(roleIndex + 1));
      } else {
        setAvailableRoles([]); // fallback if role not found
      }
    }
  }, [user]);
  const [dialup, setDialup] = useState({
    open: false,
    previousData: [],
  });
  const [dialupR, setDialupR] = useState({
    open: false,
    previousData: [],
  });

  const handleChangePage = (event, newPage) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(event.target.value, 10));
    dispatch(setPage(0));
  };

  const handleEdit = (rowData, index) => {
    setDialup({
      open: true,
      previousData: rowData,
    });
  };

  const handleClose = () => {
    setDialup({
      open: false,
      previousData: [],
    });
  };

  const handleDelete = (rowData, index) => {
    setDialupR({
      open: true,
      previousData: rowData,
    });
  };

  const handleDeleteAccount = async () => {
    const accountId = { id: dialupR?.previousData?._id };
    if (!accountId) {
      console.error("No account ID provided for deletion.");
      return;
    }
    await dispatch(DeleteUserAccountSlice(accountId));
    dispatch(GetUserAccounts(page, rowsPerPage, filters));
    setDialupR({
      open: false,
      previousData: [],
    });
  };
  return (
    <Box sx={{ pt: 6 }}>
      <Grid container alignItems="center" justifyContent="space-between" px={2}>
        <Typography
          variant="h3"
          sx={{
            fontSize: "1.875rem",
            fontWeight: 600,
            mb: 6,
            color: "text.primary",
          }}
        >
          Create User accounts
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              ml: 2,
              animation: loading ? `${rotate} 1s linear infinite` : "none",
              backgroundColor: "#3f51b5",
              color: "white",
              "&:hover": {
                backgroundColor: "#303f9f",
              },
              "&:disabled": {
                backgroundColor: "#e0e0e0",
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Typography>

        {/* <DownloadData
          headers={headers || []}
          tableData={tableData || []}
          fileName={"User-accounts-details"}
        /> */}
      </Grid>

      <Typography
        sx={{
          ml: 2,
          animation: loading ? `${rotate} 1s linear infinite` : "none",
          color: "#3f51b5",
        }}
      >
        Initiate user onboarding by specifying credentials, designation, and
        operational shift allocation.
      </Typography>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        message="Please select an export format."
        onClose={() => setOpenSnackbar(false)}
      />

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ px: 2, mt: 4 }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Controller
              name="userName"
              control={control}
              rules={{ required: "User name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="User Name"
                  fullWidth
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  error={!!errors.userName}
                  helperText={errors.userName?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name="userEmail"
              control={control}
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="User Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  error={!!errors.userEmail}
                  helperText={errors.userEmail?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name="userPassword"
              control={control}
              rules={{
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  size="small"
                  autoComplete="new-password"
                  error={!!errors.userPassword}
                  helperText={errors.userPassword?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ transition: "all 0.3s ease", width: 150 }}
              error={!!errors.userRole}
            >
              <InputLabel>Role</InputLabel>
              <Controller
                name="userRole"
                control={control}
                rules={{ required: "Role is required" }}
                render={({ field }) => (
                  <Select {...field} label="Role">
                    {availableRoles?.map((val, index) => {
                      return (
                        <MenuItem
                          key={index}
                          value={val.role}
                          className="capitalize"
                        >
                          {val.role.replaceAll("_", " ")}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              />
              {errors.userRole && (
                <Typography variant="caption" color="error">
                  {errors.userRole.message}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ transition: "all 0.3s ease", width: 150 }}
              error={!!errors.userShift}
            >
              <InputLabel>Shift</InputLabel>
              <Controller
                name="userShift"
                control={control}
                rules={{ required: "Shift is required" }}
                render={({ field }) => (
                  <Select {...field} label="Shift">
                    <MenuItem value="Morning">Morning</MenuItem>
                    <MenuItem value="Evening">Evening</MenuItem>
                    <MenuItem value="Night">Night</MenuItem>
                  </Select>
                )}
              />
              {errors.userShift && (
                <Typography variant="caption" color="error">
                  {errors.userShift.message}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <Button
                type="submit"
                variant="outlined"
                color="primary"
                disabled={isAdding}
                loading={isAdding}
                sx={{
                  borderRadius: "8px",
                  padding: "7px 16px",
                  border: "1px solid #1976d2",
                  backgroundColor: "#1994d2",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                    borderColor: "#1565c0",
                  },
                }}
              >
                Add User
              </Button>
            </motion.div>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 4 }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <Box
            sx={{
              border: "1px solid #d0d0d0",
              borderRadius: "8px",
              overflow: "hidden",
              mx: 0,
            }}
          >
            <LazyTable
              data={data}
              page={page}
              rowsPerPage={rowsPerPage}
              totalRecords={totalAccounts}
              loading={loading}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onFilterChange={(filters) => {
                dispatch(setFilters(filters));
              }}
              columns={[
                { field: "name", headerName: "User Name", filter: true },
                {
                  field: "email",
                  headerName: "Email",
                  filter: true,
                },
                {
                  field: "role",
                  headerName: "Role",
                  filter: true,
                },
                {
                  field: "shift",
                  headerName: "Shift",
                  filter: true,
                },
                {
                  field: "createdAt",
                  headerName: "Created At",
                  filter: true,
                  render: (rowData) => {
                    if (rowData.createdAt) {
                      const date = moment(rowData.createdAt);
                      if (date.isValid()) {
                        return date.format("MMM DD, YYYY hh:mm A");
                      }
                    }
                    return null;
                  },
                },
                {
                  field: "actions",
                  headerName: "Actions",
                  render: (rowData) => (
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEdit(rowData)}
                      >
                        Edit
                      </Button>
                      {user.user.role !== "manager" && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => handleDelete(rowData)}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  ),
                },
              ]}
            />
          </Box>
        </motion.div>
      </Box>

      <Dialog
        open={dialupR?.open}
        onClose={() => setDialupR({ open: false, previousData: [] })}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <DialogTitle sx={{ fontWeight: 600, flexGrow: 1, p: 0 }}>
            Edit User
          </DialogTitle>
          <IconButton
            onClick={() => setDialupR({ open: false, previousData: [] })}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ width: 420, pt: 1 }}>
          <Typography>Are you sure you want to remove this account?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialupR({ open: false, previousData: [] })}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteAccount} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <UserFormEdit
        open={dialup?.open}
        onClose={handleClose}
        previousData={dialup?.previousData || []}
      />
    </Box>
  );
}

export default CreateAccount;
